import dayjs from 'dayjs';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { eventSchema, EventEntity, EventsOrganizationListResponseApi } from '@/entities/OrganizationEvent';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { CollectionStore } from '@/stores/local/shared/CollectionStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import call from '@/utils/api';
import { pluralizeDays } from '@/utils/pluralizeDays';
import { schemeParseMultiple } from '@/utils/schemeParse';

type EventsOrganizationListStoreParams = {
  organizationId: number;
};

type PrivateFields = '_organizationId' | '_activeEventIndex' | '_total' | '_applyPreferredEventId';

export class EventsOrganizationListStore implements ILocalStore {
  readonly meta = new MetaStore();
  readonly collection = new CollectionStore<EventEntity, EventEntity>({
    parseItem: (data: EventEntity) => data,
    getEntityKey: (data: EventEntity) => String(data.id),
  });

  private _organizationId: number;
  private _activeEventIndex = 0;
  private _total = 0;
  private _preferredEventId: string | number | null = null;

  constructor({ organizationId }: EventsOrganizationListStoreParams) {
    this._organizationId = organizationId;

    makeObservable<EventsOrganizationListStore, PrivateFields>(this, {
      _organizationId: observable,
      _activeEventIndex: observable,
      _total: observable,
      events: computed,
      eventsTotal: computed,
      activeEventIndex: computed,
      nextEvent: computed,
      nextEventDateInfo: computed,
      currentEvent: computed,
      currentEventDateInfo: computed,
      fetchEvents: action.bound,
      setActiveEventIndex: action.bound,
      setPreferredEventId: action.bound,
      _applyPreferredEventId: action.bound,
      goToPreviousEvent: action.bound,
      goToNextEvent: action.bound,
    });
  }

  get events(): EventEntity[] {
    return this.collection.items;
  }

  get eventsTotal(): number {
    return this._total || this.events.length;
  }

  get activeEventIndex(): number {
    return this._activeEventIndex;
  }

  get nextEvent(): EventEntity | null {
    return this.events[0] ?? null;
  }

  get nextEventDateInfo(): { formattedDate: string; daysToEvent: number | null; remainingLabel: string } {
    return this.getEventDateInfo(this.nextEvent);
  }

  get currentEvent(): EventEntity | null {
    if (this.events.length === 0) {
      return null;
    }

    return this.events[this._activeEventIndex] ?? null;
  }

  get currentEventDateInfo(): { formattedDate: string; daysToEvent: number | null; remainingLabel: string } {
    return this.getEventDateInfo(this.currentEvent);
  }

  getEventDateInfo(event: EventEntity | null): {
    formattedDate: string;
    daysToEvent: number | null;
    remainingLabel: string;
  } {
    const date = event ? dayjs(event.startAt) : null;

    if (!date || !date.isValid()) {
      return { formattedDate: '', daysToEvent: null, remainingLabel: '— дней' };
    }

    const now = dayjs();
    const diffHoursFloat = date.diff(now, 'hour', true);
    const totalHours = Math.max(0, diffHoursFloat);
    const showHours = totalHours < 24;
    const hoursLeft = Math.max(0, Math.floor(totalHours));
    const daysLeft = Math.max(1, date.startOf('day').diff(now.startOf('day'), 'day'));

    return {
      formattedDate: date.format('D MMMM'),
      daysToEvent: showHours ? null : daysLeft,
      remainingLabel: showHours ? `осталось ${hoursLeft}ч` : `осталось ${pluralizeDays(daysLeft)}`,
    };
  }

  async fetchEvents() {
    this.meta.setLoadedStartMeta();

    const response = await call<EventsOrganizationListResponseApi>({
      endpoint: ENDPOINTS.events.list(String(this._organizationId)),
      data: {
        date_from: dayjs().toISOString(),
      },
    });

    if (response.isError) {
      this.meta.setLoadedErrorMeta();

      return;
    }

    runInAction(() => {
      const parsed = schemeParseMultiple(eventSchema, response.data.items);

      this.collection.fromArray(parsed);
      this._total = response.data.total ?? parsed.length;
      this._activeEventIndex = 0;
      this._applyPreferredEventId();

      this.meta.setLoadedSuccessMeta();
    });
  }

  setPreferredEventId(id: string | number | null) {
    this._preferredEventId = id;
    this._applyPreferredEventId();
  }

  private _applyPreferredEventId() {
    if (!this._preferredEventId || this.events.length === 0) {
      return;
    }

    const targetIndex = this.events.findIndex((event) => String(event.id) === String(this._preferredEventId));

    if (targetIndex >= 0) {
      this._activeEventIndex = targetIndex;
    }
  }

  setActiveEventIndex(index: number) {
    if (this.events.length === 0) {
      this._activeEventIndex = 0;

      return;
    }

    this._activeEventIndex = index;
  }

  goToPreviousEvent() {
    if (this.events.length <= 1) {
      return;
    }

    this._activeEventIndex = (this._activeEventIndex - 1 + this.events.length) % this.events.length;
  }

  goToNextEvent() {
    if (this.events.length <= 1) {
      return;
    }

    this._activeEventIndex = (this._activeEventIndex + 1) % this.events.length;
  }

  destroy() {
    this.meta.destroy();
    this.collection.destroy();
  }
}
